import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Bell, Send, CheckCheck, Trash2, ChevronDown, Megaphone, ShoppingCart, Star, Settings, Tag } from 'lucide-react'
import { notificationsService } from '@/services/notificationsService'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import { formatRelativeTime } from '@/utils/formatters'

const TYPE_META = {
  order:     { icon: ShoppingCart, color:'#000666', bg:'rgba(0,6,102,0.08)',    label:'طلب' },
  review:    { icon: Star,         color:'#FF6D00', bg:'rgba(255,109,0,0.08)',   label:'تقييم' },
  system:    { icon: Settings,     color:'#767683', bg:'rgba(118,118,131,0.08)', label:'النظام' },
  promotion: { icon: Tag,          color:'#1B6D24', bg:'rgba(27,109,36,0.08)',   label:'عرض' },
  message:   { icon: Send,         color:'#000666', bg:'rgba(0,6,102,0.06)',     label:'رسالة' },
}

const BROADCAST_TYPES = ['system','promotion','order','review','message']
const EMPTY_BROADCAST = { type:'system', titleAr:'', titleEn:'', bodyAr:'', bodyEn:'', toAll:true, userIds:'' }

export default function NotificationsPage() {
  const { i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()

  const [tab,         setTab]         = useState('list')
  const [typeFilter,  setTypeFilter]  = useState('')
  const [readFilter,  setReadFilter]  = useState('')
  const { page, limit, setPage }      = usePagination(1, 20)
  const [broadcast, setBroadcast]     = useState(EMPTY_BROADCAST)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notifications-admin', { type:typeFilter, isRead:readFilter, page, limit }],
    queryFn: () => notificationsService.getAll({
      type:   typeFilter || undefined,
      isRead: readFilter === 'read' ? true : readFilter === 'unread' ? false : undefined,
      page, limit,
    }),
    enabled: tab === 'list',
  })

  const notifications = data?.data || []
  const meta          = data?.meta || {}
  const unreadCount   = data?.extra?.unreadCount || 0

  const inv = () => qc.invalidateQueries({ queryKey:['notifications-admin'] })

  const markAllMut = useMutation({ mutationFn: notificationsService.markAllRead, onSuccess: () => { toast.success(`تم تعيين ${unreadCount} إشعار كمقروء`); inv() } })
  const deleteAllMut = useMutation({ mutationFn: notificationsService.deleteAll, onSuccess: () => { toast.success('تم مسح جميع الإشعارات'); inv() } })
  const markOneMut   = useMutation({ mutationFn: (id) => notificationsService.markRead(id), onSuccess: () => inv() })

  const broadcastMut = useMutation({
    mutationFn: (d) => notificationsService.broadcast(d),
    onSuccess: (data) => { toast.success(`تم الإرسال إلى ${data?.sent || '?'} مستخدم`); setBroadcast(EMPTY_BROADCAST) },
    onError: () => toast.error('فشل الإرسال'),
  })

  const handleBroadcast = () => {
    if (!broadcast.titleAr && !broadcast.titleEn) { toast.error('يرجى كتابة عنوان الإشعار'); return }
    const payload = {
      type: broadcast.type,
      title: { ar: broadcast.titleAr, en: broadcast.titleEn },
      body:  { ar: broadcast.bodyAr,  en: broadcast.bodyEn  },
      userIds: broadcast.toAll ? [] : broadcast.userIds.split(',').map((s) => s.trim()).filter(Boolean),
    }
    broadcastMut.mutate(payload)
  }

  const LBL = { display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }
  const INP = { width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Tajawal, sans-serif',outline:'none',boxSizing:'border-box',color:'#1A1C1C' }

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="hg-card p-1 flex gap-1">
        {[{ key:'list',label:'الإشعارات',icon:Bell },{ key:'broadcast',label:'إرسال إشعار',icon:Megaphone }].map(({ key,label,icon:Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ display:'flex',alignItems:'center',gap:8,padding:'8px 20px',borderRadius:12,fontSize:13,fontWeight:700,fontFamily:'Cairo, sans-serif',border:'none',cursor:'pointer',transition:'all 0.2s', background: tab===key ? '#000666' : 'transparent', color: tab===key ? '#fff' : '#767683', boxShadow: tab===key ? '0 4px 12px rgba(0,6,102,0.25)' : 'none' }}>
            <Icon size={15}/>{label}
            {key==='list' && unreadCount > 0 && <span style={{ background:'#FF6D00',color:'#fff',fontSize:11,fontWeight:700,padding:'1px 7px',borderRadius:20 }}>{unreadCount}</span>}
          </button>
        ))}
      </div>

      {tab === 'list' && (
        <>
          <div className="hg-card p-4 flex flex-wrap items-center gap-3">
            <div className="relative">
              <select className="hg-input cursor-pointer min-w-32" style={{ paddingLeft:28 }} value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}>
                <option value="">كل الأنواع</option>
                {BROADCAST_TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>)}
              </select>
              <ChevronDown size={14} style={{ position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'#767683',pointerEvents:'none' }}/>
            </div>
            <div className="relative">
              <select className="hg-input cursor-pointer min-w-32" style={{ paddingLeft:28 }} value={readFilter} onChange={(e) => { setReadFilter(e.target.value); setPage(1) }}>
                <option value="">الكل</option>
                <option value="unread">غير مقروء</option>
                <option value="read">مقروء</option>
              </select>
              <ChevronDown size={14} style={{ position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'#767683',pointerEvents:'none' }}/>
            </div>
            <div style={{ flex:1 }}/>
            {unreadCount > 0 && (
              <button onClick={() => markAllMut.mutate()} disabled={markAllMut.isPending}
                style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:10,fontSize:12,fontWeight:700,fontFamily:'Cairo, sans-serif',background:'rgba(27,109,36,0.08)',color:'#1B6D24',border:'none',cursor:'pointer' }}>
                <CheckCheck size={14}/>تعيين الكل كمقروء
              </button>
            )}
            <button onClick={() => { if(window.confirm('هل تريد حذف جميع الإشعارات؟')) deleteAllMut.mutate() }} disabled={deleteAllMut.isPending}
              style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:10,fontSize:12,fontWeight:700,fontFamily:'Cairo, sans-serif',background:'rgba(186,26,26,0.07)',color:'#BA1A1A',border:'none',cursor:'pointer' }}>
              <Trash2 size={14}/>مسح الكل
            </button>
          </div>

          <div className="hg-card overflow-hidden">
            {isLoading ? <div className="flex justify-center py-16"><LoadingSpinner/></div>
            : isError   ? <div className="flex flex-col items-center py-12 gap-3"><p style={{ color:'#BA1A1A',fontWeight:700,fontFamily:'Cairo, sans-serif' }}>فشل التحميل</p><button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button></div>
            : notifications.length === 0 ? <EmptyState icon={Bell} message="لا توجد إشعارات"/>
            : (
              <>
                <div>
                  {notifications.map((n, idx) => {
                    const meta = TYPE_META[n.type] || TYPE_META.system
                    const Icon = meta.icon
                    return (
                      <div key={n.id}
                        style={{ display:'flex',alignItems:'flex-start',gap:14,padding:'16px 20px',borderBottom: idx===notifications.length-1?'none':'1px solid #F3F3F3',background: n.isRead?'transparent':'rgba(0,6,102,0.02)',transition:'background 0.15s',cursor: n.isRead?'default':'pointer' }}
                        onClick={() => !n.isRead && markOneMut.mutate(n.id)}
                        onMouseEnter={(e) => e.currentTarget.style.background='#F9F9F9'}
                        onMouseLeave={(e) => e.currentTarget.style.background= n.isRead?'transparent':'rgba(0,6,102,0.02)'}
                      >
                        <div style={{ width:40,height:40,borderRadius:12,background:meta.bg,color:meta.color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}><Icon size={18}/></div>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:3 }}>
                            <span style={{ fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#1A1C1C' }}>
                              {n.title?.[lang] || n.title?.ar || n.title?.en || '—'}
                            </span>
                            {!n.isRead && <span style={{ width:7,height:7,borderRadius:'50%',background:'#FF6D00',flexShrink:0 }}/>}
                          </div>
                          <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:13,color:'#454652',margin:0,lineHeight:1.6,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                            {n.body?.[lang] || n.body?.ar || n.body?.en || ''}
                          </p>
                        </div>
                        <span style={{ fontFamily:'Tajawal, sans-serif',fontSize:11,color:'#767683',flexShrink:0,marginTop:2 }}>
                          {formatRelativeTime(n.createdAt, lang==='ar'?'ar-EG':'en')}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <div style={{ padding:'0 16px 16px' }}>
                  <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage}/>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {tab === 'broadcast' && (
        <div className="hg-card p-6 max-w-2xl">
          <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:24 }}>
            <div style={{ width:48,height:48,borderRadius:16,background:'rgba(255,109,0,0.1)',color:'#FF6D00',display:'flex',alignItems:'center',justifyContent:'center' }}><Megaphone size={24}/></div>
            <div>
              <h2 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',margin:0 }}>إرسال إشعار جماعي</h2>
              <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:13,color:'#767683',margin:0 }}>أرسل إشعاراً لجميع المستخدمين أو مجموعة محددة</p>
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <label style={LBL}>نوع الإشعار</label>
            <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
              {BROADCAST_TYPES.map((type) => {
                const m = TYPE_META[type]
                return (
                  <button key={type} onClick={() => setBroadcast((p) => ({ ...p,type }))}
                    style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:10,fontSize:12,fontWeight:700,fontFamily:'Cairo, sans-serif',border:'none',cursor:'pointer',transition:'all 0.2s',
                      background: broadcast.type===type ? '#000666' : '#F3F3F3',
                      color: broadcast.type===type ? '#fff' : '#454652' }}>
                    {m?.label || type}
                  </button>
                )
              })}
            </div>
          </div>

          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16 }}>
            <div><label style={LBL}>العنوان بالعربي</label><input value={broadcast.titleAr} onChange={(e) => setBroadcast((p) => ({ ...p,titleAr:e.target.value }))} placeholder="عنوان الإشعار" style={INP}/></div>
            <div><label style={LBL}>العنوان بالإنجليزي</label><input value={broadcast.titleEn} onChange={(e) => setBroadcast((p) => ({ ...p,titleEn:e.target.value }))} placeholder="Notification title" dir="ltr" style={INP}/></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16 }}>
            <div><label style={LBL}>النص بالعربي</label><textarea value={broadcast.bodyAr} onChange={(e) => setBroadcast((p) => ({ ...p,bodyAr:e.target.value }))} rows={3} placeholder="نص الإشعار..." style={{ ...INP,resize:'vertical' }}/></div>
            <div><label style={LBL}>النص بالإنجليزي</label><textarea value={broadcast.bodyEn} onChange={(e) => setBroadcast((p) => ({ ...p,bodyEn:e.target.value }))} rows={3} dir="ltr" placeholder="Notification body..." style={{ ...INP,resize:'vertical' }}/></div>
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={LBL}>المستلمون</label>
            <div style={{ display:'flex',gap:12 }}>
              <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#454652' }}>
                <input type="radio" checked={broadcast.toAll} onChange={() => setBroadcast((p) => ({ ...p,toAll:true }))} style={{ width:16,height:16 }}/>
                جميع المستخدمين النشطين
              </label>
              <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#454652' }}>
                <input type="radio" checked={!broadcast.toAll} onChange={() => setBroadcast((p) => ({ ...p,toAll:false }))} style={{ width:16,height:16 }}/>
                مستخدمون محددون
              </label>
            </div>
            {!broadcast.toAll && (
              <div style={{ marginTop:10 }}>
                <label style={LBL}>معرّفات المستخدمين (مفصولة بفاصلة)</label>
                <textarea value={broadcast.userIds} onChange={(e) => setBroadcast((p) => ({ ...p,userIds:e.target.value }))} rows={2} dir="ltr" placeholder="uuid1, uuid2, uuid3..." style={{ ...INP,resize:'none' }}/>
              </div>
            )}
          </div>

          <button onClick={handleBroadcast} disabled={broadcastMut.isPending}
            style={{ width:'100%',padding:'13px 0',borderRadius:14,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:16,color:'#fff',background:'#FF6D00',border:'none',cursor:'pointer',boxShadow:'0 8px 24px rgba(255,109,0,0.3)',display:'flex',alignItems:'center',justifyContent:'center',gap:10,transition:'all 0.15s',opacity:broadcastMut.isPending?0.7:1 }}
            onMouseEnter={(e) => !broadcastMut.isPending && (e.currentTarget.style.filter='brightness(1.1)')}
            onMouseLeave={(e) => e.currentTarget.style.filter=''}
          >
            <Send size={18}/>{broadcastMut.isPending ? 'جارٍ الإرسال...' : 'إرسال الإشعار'}
          </button>
        </div>
      )}
    </div>
  )
}
